import { initializeApp } from 'firebase/app';
import game from '../../../games';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  serverTimestamp,
  orderBy,
  limit,
  deleteDoc
} from 'firebase/firestore';
import { ShareLink } from '../../../models/ShareLink';
import { DraftVersion } from '../../../models/VersionBranch';
import { MatchReview } from '../../../models/MatchReview';

// COMMERCIAL-DEMO: hard kill switch for all user-draft cloud writes.
// Demo users' draft preparation must stay strictly on their own machine —
// no Firestore sync (see SupabaseService.js counterpart). Auth still works.
const CLOUD_SAVE_DISABLED = true;

// Firebase configuration comes from the active game's config so that
// games reusing this implementation (hok/lol use the dota tree) init
// with their own project instead of this game's.
const firebaseConfig = game.firebase;

// Demo mode flag - set to false to use real Firebase
export const DEMO_MODE = false;

// Storage for demo data
export const demoStorage = {
  users: {},
  drafts: {}
};

// Initialize Firebase only if not in demo mode
let app, auth, db, googleProvider;

if (!DEMO_MODE) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  googleProvider = new GoogleAuthProvider();
} else {
  console.log('🧪 Running in DEMO MODE - no Firebase connection required');
  // Still create the GoogleAuthProvider for consistency
  googleProvider = new GoogleAuthProvider();
}

// Export auth objects for use elsewhere
export { auth, db, googleProvider };

class FirebaseService {
  constructor() {
    this.demoMode = DEMO_MODE;
    this.demoStorage = demoStorage;
  }

  // Authentication methods
  async signIn(email, password) {
    if (this.demoMode) {
      console.log('📝 Demo login with:', email);
      if (this.demoStorage.users[email] && this.demoStorage.users[email].password === password) {
        return { user: this.demoStorage.users[email] };
      } else {
        throw new Error('Invalid email or password');
      }
    } else {
      // First sign in with email and password
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Then check if the email is allowed
      const allowedEmailRef = doc(db, 'allowedEmails', email);
      const docSnap = await getDoc(allowedEmailRef);

      if (!docSnap.exists()) {
        // Check if the user is an admin
        const isAdmin = await this.isUserAdmin(result.user.uid);
        if (!isAdmin) {
          // If not allowed and not admin, sign out and throw error
          await signOut(auth);
          throw new Error('Access denied. Your email is not authorized to use this application.');
        }
      }

      // Ensure user document exists in Firestore
      const userDocRef = doc(db, 'users', result.user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        // Create user document if it doesn't exist
        console.log('Creating user document for email sign-in:', email);
        await setDoc(userDocRef, {
          displayName: result.user.displayName || email.split('@')[0],
          email: result.user.email,
          createdAt: serverTimestamp(),
          tier: 'free', // Default to free tier
          isAdmin: false,
          matchPullsToday: 0,
          lastMatchPullDate: null,
          lastActive: serverTimestamp()
        });
      } else {
        // User document exists, just update lastActive
        console.log('Updating lastActive for email sign-in:', email);
        await updateDoc(userDocRef, {
          lastActive: serverTimestamp()
        });
      }

      return result;
    }
  }

  async signUp(email, password, displayName) {
    if (this.demoMode) {
      console.log('📝 Demo signup with:', email);
      if (this.demoStorage.users[email]) {
        throw new Error('User already exists');
      }
      
      // Create demo user
      const newUser = {
        uid: email,
        email,
        displayName,
        password
      };
      
      this.demoStorage.users[email] = newUser;
      this.demoStorage.drafts[email] = {};
      
      return { user: newUser };
    } else {
      // First check if the email is in the allowed list
      const allowedEmailRef = doc(db, 'allowedEmails', email);
      const docSnap = await getDoc(allowedEmailRef);
      
      if (!docSnap.exists()) {
        throw new Error('Registration is restricted. Your email is not authorized to register for this application.');
      }
      
      // If allowed, proceed with registration
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Save additional user info with default free tier
      await setDoc(doc(db, 'users', result.user.uid), {
        displayName,
        email,
        createdAt: new Date().toISOString(),
        tier: 'free', // Default to free tier
        isAdmin: false,
        matchPullsToday: 0,
        lastMatchPullDate: null
      });
      return result;
    }
  }

  async signOut() {
    if (this.demoMode) {
      // In demo mode, don't actually sign out
      console.log('📝 Demo sign out');
      window.location.href = '/';
      return Promise.resolve();
    }
    await signOut(auth);
    // Redirect to home page
    window.location.href = '/';
    return Promise.resolve();
  }

  // Google sign in
  async signInWithGoogle() {
    if (this.demoMode) {
      console.log('📝 Demo Google login');
      return { 
        user: {
          uid: 'demo-user-123',
          email: 'demo@example.com',
          displayName: 'Demo User'
        } 
      };
    } else {
      try {
        let result;
        if (typeof window !== 'undefined' && window.draftplanDesktop && window.draftplanDesktop.googleSignIn) {
          // Desktop (Electron): Google blocks OAuth inside embedded webviews, so do
          // the handshake in the real system browser (loopback + PKCE) and federate
          // the returned Google id_token directly. Web build keeps using the popup.
          const { idToken } = await window.draftplanDesktop.googleSignIn();
          result = await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
        } else {
          result = await signInWithPopup(auth, googleProvider);
        }
        const user = result.user;
        
        // Check if the user's email is allowed
        const allowedEmailRef = doc(db, 'allowedEmails', user.email);
        const docSnap = await getDoc(allowedEmailRef);
        
        if (!docSnap.exists()) {
          // Check if the user is an admin
          const isAdmin = await this.isUserAdmin(user.uid);
          if (!isAdmin) {
            // If not allowed and not admin, sign out and throw error
            await signOut(auth);
            throw new Error('Access denied. Your email is not authorized to use this application.');
          }
        }
        
        // Ensure user document exists in Firestore
        const userDocRef = doc(db, 'users', result.user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
          // Create user document if it doesn't exist
          console.log('Creating user document for Google sign-in:', result.user.email);
          await setDoc(userDocRef, {
            displayName: result.user.displayName,
            email: result.user.email,
            createdAt: serverTimestamp(),
            tier: 'free', // Default to free tier
            isAdmin: false,
            matchPullsToday: 0,
            lastMatchPullDate: null,
            lastActive: serverTimestamp()
          });
        } else {
          // User document exists, just update lastActive
          console.log('Updating lastActive for Google sign-in:', result.user.email);
          await updateDoc(userDocRef, {
            lastActive: serverTimestamp()
          });
        }

        return result;
      } catch (error) {
        console.error("Error signing in with Google:", error);
        throw error;
      }
    }
  }

  // Current user
  getCurrentUser() {
    if (this.demoMode) {
      // In demo mode, always return a fake user
      return {
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User'
      };
    }
    return auth.currentUser;
  }

  // Check if the current user's email is in the allowed list
  async checkCurrentUserAccess() {
    if (this.demoMode) {
      return true; // In demo mode, always grant access
    }
    
    try {
      const user = this.getCurrentUser();
      if (!user) return false;
      
      // Check if the user's email is in the allowedEmails collection
      const allowedEmailRef = doc(db, 'allowedEmails', user.email);
      const docSnap = await getDoc(allowedEmailRef);
      
      if (docSnap.exists()) {
        console.log('User email is in allowed list:', user.email);
        return true;
      }
      
      // If not found, check if the user is an admin (admins always have access)
      const isAdmin = await this.isUserAdmin(user.uid);
      if (isAdmin) {
        console.log('User is an admin, granting access:', user.email);
        return true;
      }
      
      console.log('User email not in allowed list:', user.email);
      return false;
    } catch (error) {
      console.error('Error checking user access:', error);
      return false;
    }
  }
  
  // Check if a user is an admin
  async isUserAdmin(userId) {
    if (this.demoMode) {
      return userId === 'demo-user-123'; // Only the demo user is admin in demo mode
    }
    
    try {
      const userDocRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userDocRef);
      
      if (docSnap.exists()) {
        // Check if the user has an admin field set to true
        return docSnap.data().isAdmin === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  // Add onAuthStateChanged method
  onAuthStateChanged(callback) {
    if (this.demoMode) {
      // In demo mode, immediately call callback with demo user and return a no-op unsubscribe function
      callback({
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User'
      });
      return () => {};
    }
    
    // Use Firebase's onAuthStateChanged
    return onAuthStateChanged(auth, callback);
  }

  // Draft operations
  async saveDraft(draftData) {
    // COMMERCIAL-DEMO: cloud saving disabled — drafts never leave this machine.
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore saveDraft skipped (commercial-demo: local-only build)');
      return draftData.id || ('local_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8));
    }
    console.log('🔥 Firebase saveDraft called');
    console.log('🔥 Draft data received:', draftData);
    try {
      const user = this.getCurrentUser();
      console.log('🔥 Current user:', user);
      if (!user) {
        throw new Error("User must be logged in to save a draft");
      }

      // Make sure userId is set
      draftData.userId = user.uid;
      draftData.userEmail = user.email;
      console.log('🔥 Added user info to draft - userId:', user.uid, 'email:', user.email);
      
      // Update timestamp
      draftData.updatedAt = new Date().toISOString();
      if (!draftData.createdAt) {
        draftData.createdAt = draftData.updatedAt;
      }
      
      // Set default visibility to private if not specified
      if (draftData.isPublic === undefined) {
        draftData.isPublic = false;
      }
      
      // Convert drafts to the correct format
      // This mirrors the _convertDraftsToMap method in the Flutter app
      if (draftData.radiantDrafts) {
        draftData.radiantDrafts = this._convertDraftsToMap(draftData.radiantDrafts, true);
      }
      
      if (draftData.direDrafts) {
        draftData.direDrafts = this._convertDraftsToMap(draftData.direDrafts, false);
      }
      
      // Extract core hero data for display in cards
      if (draftData.radiantHeroes && Array.isArray(draftData.radiantHeroes)) {
        // Store the picks and bans separately for display in cards
        draftData.radiantPicks = draftData.radiantHeroes
          .filter(hero => hero && !hero.isBanned)
          .map(hero => hero.hero);
          
        draftData.radiantBans = draftData.radiantHeroes
          .filter(hero => hero && hero.isBanned)
          .map(hero => hero.hero);
      }
      
      if (draftData.direHeroes && Array.isArray(draftData.direHeroes)) {
        // Store the picks and bans separately for display in cards
        draftData.direPicks = draftData.direHeroes
          .filter(hero => hero && !hero.isBanned)
          .map(hero => hero.hero);
          
        draftData.direBans = draftData.direHeroes
          .filter(hero => hero && hero.isBanned)
          .map(hero => hero.hero);
      }

      console.log('🔥 Demo mode?', this.demoMode);

      if (this.demoMode) {
        // Demo mode save draft
        // Generate an ID if it doesn't exist
        if (!draftData.id) {
          draftData.id = 'draft_' + Date.now();
        }

        // Initialize drafts collection if it doesn't exist
        if (!demoStorage.drafts[user.uid]) {
          demoStorage.drafts[user.uid] = {};
        }

        // Save to demo storage - only visible to the owner unless explicitly marked public
        demoStorage.drafts[user.uid][draftData.id] = draftData;
        console.log('🔥 Demo mode: Saved draft', draftData.id, draftData.isPublic ? '(public)' : '(private)');
        return draftData.id;
      } else {
        console.log('🔥 Real Firebase mode - saving to Firestore');
        // Real Firebase implementation
        // Primary storage is in user's collection for privacy control
        let draftId = draftData.id;
        console.log('🔥 Draft ID:', draftId);

        if (draftId) {
          console.log('🔥 Updating existing draft:', draftId);
          // Update existing draft in user's collection first (primary storage)
          const userDraftRef = doc(db, 'users', user.uid, 'drafts', draftId);
          console.log('🔥 Firestore path: users/', user.uid, '/drafts/', draftId);
          await setDoc(userDraftRef, draftData, { merge: true });
          console.log('🔥 ✅ Draft updated in Firestore!');

          // If public, also update in main collection for discoverability
          if (draftData.isPublic) {
            const draftDocRef = doc(db, 'drafts', draftId);
            await setDoc(draftDocRef, draftData, { merge: true });
            console.log('🔥 Updated draft in both locations (public)', draftId);
          } else {
            // If private, remove from public collection if it exists there
            try {
              const draftDocRef = doc(db, 'drafts', draftId);
              const draftSnap = await getDoc(draftDocRef);

              if (draftSnap.exists() && draftSnap.data().userId === user.uid) {
                await deleteDoc(draftDocRef);
                console.log('🔥 Made draft private, removed from public collection', draftId);
              }
            } catch (error) {
              console.error('🔥 Error checking/removing from public collection:', error);
            }

            console.log('🔥 Updated draft in user collection only (private)', draftId);
          }
        } else {
          console.log('🔥 Creating new draft (no ID provided)');
          // Create new draft with generated ID
          // Always save in user's collection first
          const userDraftsCollection = collection(db, 'users', user.uid, 'drafts');
          console.log('🔥 Firestore collection path: users/', user.uid, '/drafts');
          const newUserDocRef = await addDoc(userDraftsCollection, draftData);
          draftId = newUserDocRef.id;
          console.log('🔥 ✅ New draft created with ID:', draftId);

          // Update the draft with its ID
          draftData.id = draftId;
          await updateDoc(newUserDocRef, { id: draftId });
          console.log('🔥 Updated draft document with ID field');

          // If public, also save in main collection for discoverability
          if (draftData.isPublic) {
            const draftDocRef = doc(db, 'drafts', draftId);
            await setDoc(draftDocRef, draftData);
            console.log('🔥 Created new draft in both locations (public)', draftId);
          } else {
            console.log('🔥 Created new draft in user collection only (private)', draftId);
          }
        }
        
        return draftId;
      }
    } catch (error) {
      console.error("❌❌❌ Error saving draft:", error);
      console.error("❌ Error code:", error.code);
      console.error("❌ Error message:", error.message);
      console.error("❌ Full error object:", JSON.stringify(error, null, 2));
      throw error;
    }
  }

  // Helper method to convert drafts to the format expected by the app
  // Mirrors the _convertDraftsToMap method in the Flutter app
  _convertDraftsToMap(drafts, isRadiant) {
    const draftMap = {};
    
    // Get sequence maps (same as in the Flutter app)
    const radiantSequenceMap = {
      0: '1', 1: '4', 2: '7', 3: '8', 4: '10', 5: '11',
      6: '14', 7: '15', 8: '18', 9: '19', 10: '22', 11: '23'
    };
    
    const direSequenceMap = {
      0: '2', 1: '3', 2: '5', 3: '6', 4: '9', 5: '12',
      6: '13', 7: '16', 8: '17', 9: '20', 10: '21', 11: '24'
    };
    
    const sequenceMap = isRadiant ? radiantSequenceMap : direSequenceMap;
    
    for (let i = 0; i < drafts.length; i++) {
      const scenarioMap = {};
      
      if (Array.isArray(drafts[i])) {
        // Process heroes - use the appropriate offset depending on whether it's radiant or dire
        const startIndex = isRadiant ? 0 : 0;
        const endIndex = isRadiant ? 12 : 24;
        
        for (let j = startIndex; j < endIndex; j++) {
          if (j < drafts[i].length && drafts[i][j]) {
            const heroData = drafts[i][j];
            if (heroData) {
              // Store by index
              scenarioMap[j.toString()] = heroData;
              
              // Also store by sequence number
              const idx = isRadiant ? j : j - 12;
              const seq = sequenceMap[idx];
              if (seq) {
                scenarioMap[seq] = heroData;
              }
            }
          }
        }
      }
      
      draftMap[i.toString()] = scenarioMap;
    }
    
    return draftMap;
  }

  // Format draft data for consumption by the app
  _formatDraftData(draftDoc) {
    const data = draftDoc.data ? draftDoc.data() : draftDoc;
    const id = draftDoc.id || data.id;

    return {
      id,
      title: data.title || '',
      tournament: data.tournament || '',
      teamName: data.teamName || '',  // Added teamName
      stage: data.stage || '',  // Added stage
      date: data.date || '',
      updatedAt: data.updatedAt || new Date().toISOString(),
      createdAt: data.createdAt || data.updatedAt || new Date().toISOString(),
      userId: data.userId || '',
      userEmail: data.userEmail || '',
      scenariosFirstPick: data.scenariosFirstPick || [],  // Added scenariosFirstPick
      scenariosSecondPick: data.scenariosSecondPick || [],  // Added scenariosSecondPick
      radiantHeroes: data.radiantHeroes || [],
      direHeroes: data.direHeroes || [],
      radiantDrafts: data.radiantDrafts || {},
      direDrafts: data.direDrafts || {},
      radiantPicks: data.radiantPicks || [],
      direPicks: data.direPicks || [],
      radiantBans: data.radiantBans || [],
      direBans: data.direBans || [],
      isPublic: data.isPublic || false
    };
  }

  async getDraft(draftId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore getDraft skipped (commercial-demo: local-only build)');
      return null;
    }
    try {
      const user = this.getCurrentUser();

      if (this.demoMode) {
        // Demo mode get draft
        if (!user) {
          console.log('Demo mode: User must be logged in to access drafts');
          return null;
        }
        
        // First check if this is the user's own draft
        const userId = user.uid;
        if (demoStorage.drafts[userId] && demoStorage.drafts[userId][draftId]) {
          console.log('Demo mode: Found draft for user', userId, draftId);
          return this._formatDraftData(demoStorage.drafts[userId][draftId]);
        }
        
        // If not found in user's drafts, check if it's a public draft from other users
        for (const otherUserId in demoStorage.drafts) {
          if (otherUserId !== userId && 
              demoStorage.drafts[otherUserId][draftId] && 
              demoStorage.drafts[otherUserId][draftId].isPublic) {
            console.log('Demo mode: Found public draft from another user', draftId);
            return this._formatDraftData(demoStorage.drafts[otherUserId][draftId]);
          }
        }
        
        console.log('Demo mode: Draft not found or not accessible', draftId);
        return null;
      } else {
        // Real Firebase implementation - check both potential locations
        if (!user) {
          console.log('Firebase: User must be logged in to access drafts');
          return null;
        }
        
        // First, check if it's the user's own draft
        // Try user-specific path first (preferred)
          const userDraftRef = doc(db, "users", user.uid, "drafts", draftId);
          const userDraftSnap = await getDoc(userDraftRef);
          
          if (userDraftSnap.exists()) {
            console.log('Firebase: Found draft at /users/{uid}/drafts path', draftId);
            return this._formatDraftData({
              id: userDraftSnap.id,
              data: () => userDraftSnap.data()
          });
        }
        
        // Try the direct /drafts/{draftId} path and check ownership
        const draftRef = doc(db, "drafts", draftId);
        const draftSnap = await getDoc(draftRef);
        
        if (draftSnap.exists()) {
          const draftData = draftSnap.data();
          
          // Check if this is the user's own draft
          if (draftData.userId === user.uid) {
            console.log('Firebase: Found user draft at /drafts path', draftId);
            return this._formatDraftData({
              id: draftSnap.id,
              data: () => draftData
            });
          }
          
          // Check if it's a public draft
          if (draftData.isPublic === true) {
            console.log('Firebase: Found public draft from another user', draftId);
            return this._formatDraftData({
              id: draftSnap.id,
              data: () => draftData
            });
          }
        }
        
        // Check public drafts by querying
        console.log('Firebase: Draft not found, searching in public drafts', draftId);
        const draftsRef = collection(db, "drafts");
        const q = query(
          draftsRef,
          where("isPublic", "==", true),
          where("id", "==", draftId)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const publicDraft = querySnapshot.docs[0];
          console.log('Firebase: Found public draft', draftId);
          return this._formatDraftData({
            id: publicDraft.id,
            data: () => publicDraft.data()
          });
        }
        
        console.log('Firebase: Draft not found or not accessible', draftId);
        return null;
      }
    } catch (error) {
      console.error("Error getting draft:", error);
      throw error;
    }
  }

  async getUserDrafts(userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore getUserDrafts skipped (commercial-demo: local-only build)');
      return [];
    }
    const currentUser = this.getCurrentUser();
    console.log('🔍 getUserDrafts: Called with userId:', userId);
    console.log('🔍 getUserDrafts: currentUser:', currentUser);
    console.log('🔍 getUserDrafts: currentUser.uid:', currentUser?.uid);
    console.log('🔍 getUserDrafts: Match?', userId === currentUser?.uid);

    if (!currentUser) {
      console.error('❌ getUserDrafts: No user logged in!');
      throw new Error("User must be logged in to get drafts");
    }

    // Security check - users can only see their own drafts or public drafts
    if (userId !== currentUser.uid) {
      console.log('⚠️ getUserDrafts: User ID mismatch! Requested:', userId, 'Current:', currentUser.uid);
      console.log('Attempted to access another user\'s drafts. Redirecting to public drafts.');
      return this.getPublicDrafts();
    }

    if (this.demoMode) {
      console.log('📝 Getting user drafts in demo mode for:', userId);
      const userDrafts = this.demoStorage.drafts[userId] || {};
      return Object.values(userDrafts)
        .map(draft => this._formatDraftData(draft))
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      // Real Firebase implementation - use user collection for privacy
      console.log('🔥 Firebase: Getting user drafts for userId:', userId);
      console.log('🔥 Firebase: Collection path: users/' + userId + '/drafts');
      const userDraftsRef = collection(db, "users", userId, "drafts");
      const userDraftsSnapshot = await getDocs(userDraftsRef);

      console.log('🔥 Firebase: Found', userDraftsSnapshot.docs.length, 'draft documents in Firestore');

      if (userDraftsSnapshot.docs.length === 0) {
        console.warn('⚠️ Firebase: No drafts found! This could mean:');
        console.warn('  1. User has not saved any drafts yet');
        console.warn('  2. Wrong user ID is being used');
        console.warn('  3. Firestore rules are blocking access');
      }

      const drafts = userDraftsSnapshot.docs.map(doc => {
        console.log('🔥 Firebase: Processing draft doc ID:', doc.id);
        const data = doc.data();
        console.log('🔥 Firebase: Draft data keys:', Object.keys(data));
        console.log('🔥 Firebase: teamName:', data.teamName, 'tournament:', data.tournament);
        return this._formatDraftData({
          id: doc.id,
          data: () => doc.data()
        });
      });

      console.log('🔥 Firebase: Formatted', drafts.length, 'drafts for display');

      return drafts.sort((a, b) =>
        new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    }
  }

  async getPublicDrafts() {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore getPublicDrafts skipped (commercial-demo: local-only build)');
      return [];
    }
    const user = this.getCurrentUser();
    if (!user) {
      throw new Error("User must be logged in to view public drafts");
    }

    if (this.demoMode) {
      console.log('📝 Getting public drafts in demo mode');
      const publicDrafts = [];
      
      // Collect all public drafts from all users
      for (const userId in this.demoStorage.drafts) {
        // Skip current user's drafts - they'll see those in their getDrafts() call
        if (userId === user.uid) continue;
        
        const userDrafts = this.demoStorage.drafts[userId];
        for (const draftId in userDrafts) {
          // Only include drafts explicitly marked as public
          if (userDrafts[draftId].isPublic === true) {
            publicDrafts.push(this._formatDraftData(userDrafts[draftId]));
          }
        }
      }
      
      return publicDrafts.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      // Real Firebase implementation - only get drafts from the public collection
      // The public collection only contains drafts explicitly marked as public
      const draftsRef = collection(db, 'drafts');
      const q = query(
        draftsRef,
        where('isPublic', '==', true),
        // Don't show the user's own drafts here, they'll see those in getDrafts()
        where('userId', '!=', user.uid),
        orderBy('userId'), // Required for inequality filter above
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => 
        this._formatDraftData({
          id: doc.id,
          data: () => doc.data()
        })
      );
    }
  }

  async deleteDraft(draftId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore deleteDraft skipped (commercial-demo: local-only build)');
      return true;
    }
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error("User must be logged in to delete a draft");
      }

      if (this.demoMode) {
        // Demo mode delete draft
        const userId = user.uid;
        
        if (!demoStorage.drafts[userId] || !demoStorage.drafts[userId][draftId]) {
          throw new Error("Draft not found or you don't have permission to delete it");
        }
        
        delete demoStorage.drafts[userId][draftId];
        console.log('Demo mode: Deleted draft', draftId);
        return true;
      } else {
        // Real Firebase delete draft - first check user's collection (primary storage)
        const userDraftRef = doc(db, "users", user.uid, "drafts", draftId);
        const userDraftSnap = await getDoc(userDraftRef);
        
        if (userDraftSnap.exists()) {
          // Delete from user's collection
          await deleteDoc(userDraftRef);
          console.log('Firebase: Deleted draft from user collection', draftId);
          
          // Also try to delete from main collection if it exists there
          try {
            const publicDocRef = doc(db, "drafts", draftId);
            const publicDocSnap = await getDoc(publicDocRef);
            
            if (publicDocSnap.exists() && publicDocSnap.data().userId === user.uid) {
              await deleteDoc(publicDocRef);
              console.log('Firebase: Also deleted draft from public collection', draftId);
            }
          } catch (error) {
            console.error("Error checking/deleting from public collection:", error);
          }
          
          return true;
        }
        
        // If not in user's collection, check main collection
        const docRef = doc(db, "drafts", draftId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          throw new Error("Draft not found");
        }
        
        const draftData = docSnap.data();
        
        if (draftData.userId !== user.uid) {
          throw new Error("You don't have permission to delete this draft");
        }
        
        // Delete the document from main collection
        await deleteDoc(docRef);
        console.log('Firebase: Deleted draft from public collection only', draftId);
        return true;
      }
    } catch (error) {
      console.error("Error deleting draft:", error);
      throw error;
    }
  }

  async getDrafts() {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore getDrafts skipped (commercial-demo: local-only build)');
      return [];
    }
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error("User must be logged in to get drafts");
      }

      if (this.demoMode) {
        // Demo mode get drafts
        const userId = user.uid;
        
        // Only get drafts that belong to this user
        const userDrafts = demoStorage.drafts[userId] || {};
        
        // Convert object to array with IDs properly set
        const draftsArray = Object.entries(userDrafts).map(([id, draft]) => 
          this._formatDraftData({...draft, id})
        );
        
        console.log('Demo mode: Retrieved drafts for user', userId, draftsArray.length);
        return draftsArray.sort((a, b) => 
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      } else {
        // Real Firebase get drafts - ONLY get drafts belonging to current user
        const drafts = [];
        const draftIds = new Set(); // To track unique drafts
        
        try {
          // First, get drafts from user-specific collection (preferred storage)
          const userDraftsRef = collection(db, "users", user.uid, "drafts");
          const userDraftsSnapshot = await getDocs(userDraftsRef);
          
          userDraftsSnapshot.forEach((doc) => {
            const draft = this._formatDraftData({
              id: doc.id,
              data: () => doc.data()
            });
            drafts.push(draft);
            draftIds.add(doc.id);
          });
          
          console.log('Firebase: Retrieved drafts from user collection', drafts.length);
        } catch (error) {
          console.error('Error getting drafts from user collection:', error);
        }
        
        try {
          // Then, get drafts from main collection that belong to this user
          const draftsRef = collection(db, "drafts");
          const q = query(
            draftsRef,
            where("userId", "==", user.uid),
            orderBy("updatedAt", "desc")
          );
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach((doc) => {
            // Only add if not already in the list
            if (!draftIds.has(doc.id)) {
              const draft = this._formatDraftData({
                id: doc.id,
                data: () => doc.data()
              });
              drafts.push(draft);
              draftIds.add(doc.id);
            }
          });
          
          console.log('Firebase: Retrieved drafts including main collection', drafts.length);
        } catch (error) {
          console.error('Error getting drafts from main collection:', error);
        }
        
        // Sort by updatedAt timestamp
        return drafts.sort((a, b) => 
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      }
    } catch (error) {
      console.error("Error getting drafts:", error);
      throw error;
    }
  }

  // Add an email to the allowed list
  async addAllowedEmail(email) {
    if (!email) {
      throw new Error('Email is required');
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email format');
    }
    
    if (this.demoMode) {
      console.log('📝 Demo mode: Adding email to allowed list:', email);
      return true;
    }
    
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('You must be logged in to add allowed emails');
      }
      
      // Check if the current user is an admin
      const isAdmin = await this.isUserAdmin(user.uid);
      if (!isAdmin) {
        throw new Error('Only administrators can add allowed emails');
      }
      
      // Add the email to the allowedEmails collection
      await setDoc(doc(db, 'allowedEmails', email), {
        addedAt: serverTimestamp(),
        addedBy: user.email
      });
      
      console.log('Added email to allowed list:', email);
      return true;
    } catch (error) {
      console.error('Error adding allowed email:', error);
      throw error;
    }
  }

  // ============= VERSION BRANCHES =============

  async saveVersion(draftId, version) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore saveVersion skipped (commercial-demo: local-only build)');
      return version?.versionId || null;
    }
    if (this.demoMode) {
      console.log('📝 Demo mode: Saving version:', version.versionId);
      const key = `${draftId}_versions`;
      if (!this.demoStorage[key]) {
        this.demoStorage[key] = [];
      }
      this.demoStorage[key].push(version.toJSON());
      return version.versionId;
    }

    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      const versionRef = doc(db, 'draftVersions', version.versionId);
      await setDoc(versionRef, {
        ...version.toJSON(),
        userId: user.uid,
        createdAt: serverTimestamp(),
      });

      console.log('Version saved:', version.versionId);
      return version.versionId;
    } catch (error) {
      console.error('Error saving version:', error);
      throw error;
    }
  }

  async getVersions(draftId) {
    if (this.demoMode) {
      const key = `${draftId}_versions`;
      const versions = this.demoStorage[key] || [];
      return versions.map(v => DraftVersion.fromJSON(v));
    }

    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      const q = query(
        collection(db, 'draftVersions'),
        where('draftId', '==', draftId),
        where('userId', '==', user.uid),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const versions = [];
      querySnapshot.forEach(doc => {
        versions.push(DraftVersion.fromJSON({ id: doc.id, ...doc.data() }));
      });

      return versions;
    } catch (error) {
      console.error('Error fetching versions:', error);
      throw error;
    }
  }

  async deleteVersion(versionId) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Deleting version:', versionId);
      Object.keys(this.demoStorage).forEach(key => {
        if (key.endsWith('_versions')) {
          const versions = this.demoStorage[key];
          this.demoStorage[key] = versions.filter(v => v.versionId !== versionId);
        }
      });
      return;
    }

    try {
      await deleteDoc(doc(db, 'draftVersions', versionId));
      console.log('Version deleted:', versionId);
    } catch (error) {
      console.error('Error deleting version:', error);
      throw error;
    }
  }

  // ============= SHARE LINKS =============

  async createShareLink(shareLink) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore createShareLink skipped (commercial-demo: local-only build)');
      throw new Error('Sharing is disabled in this build — drafts stay on your machine only.');
    }
    if (this.demoMode) {
      console.log('📝 Demo mode: Creating share link:', shareLink.shareToken);
      const key = 'shareLinks';
      if (!this.demoStorage[key]) {
        this.demoStorage[key] = {};
      }
      this.demoStorage[key][shareLink.shareToken] = shareLink.toJSON();
      return shareLink.shareToken;
    }

    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      const shareLinkRef = doc(db, 'shareLinks', shareLink.shareToken);
      await setDoc(shareLinkRef, {
        ...shareLink.toJSON(),
        createdAt: serverTimestamp(),
      });

      console.log('Share link created:', shareLink.shareToken);
      return shareLink.shareToken;
    } catch (error) {
      console.error('Error creating share link:', error);
      throw error;
    }
  }

  async getShareLink(shareToken) {
    if (this.demoMode) {
      const shareLinks = this.demoStorage.shareLinks || {};
      const link = shareLinks[shareToken];
      return link ? ShareLink.fromJSON(link) : null;
    }

    try {
      const shareLinkRef = doc(db, 'shareLinks', shareToken);
      const docSnap = await getDoc(shareLinkRef);

      if (docSnap.exists()) {
        return ShareLink.fromJSON({ id: docSnap.id, ...docSnap.data() });
      }
      return null;
    } catch (error) {
      console.error('Error fetching share link:', error);
      throw error;
    }
  }

  async getShareLinksForDraft(draftId) {
    if (this.demoMode) {
      const shareLinks = this.demoStorage.shareLinks || {};
      return Object.values(shareLinks)
        .filter(link => link.draftId === draftId)
        .map(link => ShareLink.fromJSON(link));
    }

    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      const q = query(
        collection(db, 'shareLinks'),
        where('draftId', '==', draftId),
        where('createdBy', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const shareLinks = [];
      querySnapshot.forEach(doc => {
        shareLinks.push(ShareLink.fromJSON({ id: doc.id, ...doc.data() }));
      });

      return shareLinks;
    } catch (error) {
      console.error('Error fetching share links:', error);
      throw error;
    }
  }

  async getAllShareLinksForUser() {
    if (this.demoMode) {
      const shareLinks = this.demoStorage.shareLinks || {};
      return Object.values(shareLinks)
        .map(link => ShareLink.fromJSON(link))
        .sort((a, b) => b.createdAt - a.createdAt);
    }

    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      // Query without orderBy to avoid needing composite index
      const q = query(
        collection(db, 'shareLinks'),
        where('createdBy', '==', user.uid)
      );

      const querySnapshot = await getDocs(q);
      const shareLinks = [];
      querySnapshot.forEach(doc => {
        shareLinks.push(ShareLink.fromJSON({ id: doc.id, ...doc.data() }));
      });

      // Sort in JavaScript instead of Firestore
      return shareLinks.sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      console.error('Error fetching all share links:', error);
      throw error;
    }
  }

  async updateShareLink(shareToken, updates) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Updating share link:', shareToken);
      const shareLinks = this.demoStorage.shareLinks || {};
      if (shareLinks[shareToken]) {
        shareLinks[shareToken] = { ...shareLinks[shareToken], ...updates };
      }
      return;
    }

    try {
      const shareLinkRef = doc(db, 'shareLinks', shareToken);
      await updateDoc(shareLinkRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      console.log('Share link updated:', shareToken);
    } catch (error) {
      console.error('Error updating share link:', error);
      throw error;
    }
  }

  async recordShareLinkAccess(shareToken, accessInfo) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Recording access for:', shareToken);
      const shareLinks = this.demoStorage.shareLinks || {};
      if (shareLinks[shareToken]) {
        shareLinks[shareToken].viewCount = (shareLinks[shareToken].viewCount || 0) + 1;
        shareLinks[shareToken].lastAccessedAt = Date.now();
        if (!shareLinks[shareToken].accessLog) {
          shareLinks[shareToken].accessLog = [];
        }
        shareLinks[shareToken].accessLog.push(accessInfo);
      }
      return;
    }

    try {
      const shareLinkRef = doc(db, 'shareLinks', shareToken);
      const docSnap = await getDoc(shareLinkRef);

      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const accessLog = currentData.accessLog || [];
        const uniqueViewers = currentData.uniqueViewers || [];

        if (accessInfo.userId && !uniqueViewers.includes(accessInfo.userId)) {
          uniqueViewers.push(accessInfo.userId);
        }

        accessLog.push(accessInfo);
        const trimmedLog = accessLog.slice(-100);

        await updateDoc(shareLinkRef, {
          viewCount: (currentData.viewCount || 0) + 1,
          lastAccessedAt: serverTimestamp(),
          uniqueViewers,
          accessLog: trimmedLog,
        });
      }
    } catch (error) {
      console.error('Error recording share link access:', error);
      throw error;
    }
  }

  async deleteShareLink(shareToken) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Deleting share link:', shareToken);
      const shareLinks = this.demoStorage.shareLinks || {};
      delete shareLinks[shareToken];
      return;
    }

    try {
      await deleteDoc(doc(db, 'shareLinks', shareToken));
      console.log('Share link deleted:', shareToken);
    } catch (error) {
      console.error('Error deleting share link:', error);
      throw error;
    }
  }

  // ============= MATCH REVIEWS =============

  async saveMatchReview(review) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore saveMatchReview skipped (commercial-demo: local-only build)');
      return review?.reviewId || null;
    }
    if (this.demoMode) {
      console.log('📝 Demo mode: Saving match review:', review.reviewId);
      const key = 'matchReviews';
      if (!this.demoStorage[key]) {
        this.demoStorage[key] = {};
      }
      this.demoStorage[key][review.reviewId] = review.toJSON();
      return review.reviewId;
    }

    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      const reviewRef = doc(db, 'matchReviews', review.reviewId);
      await setDoc(reviewRef, {
        ...review.toJSON(),
        userId: user.uid,
        createdAt: serverTimestamp(),
      });

      console.log('Match review saved:', review.reviewId);
      return review.reviewId;
    } catch (error) {
      console.error('Error saving match review:', error);
      throw error;
    }
  }

  async getMatchReview(reviewId) {
    if (this.demoMode) {
      const reviews = this.demoStorage.matchReviews || {};
      const review = reviews[reviewId];
      return review ? MatchReview.fromJSON(review) : null;
    }

    try {
      const reviewRef = doc(db, 'matchReviews', reviewId);
      const docSnap = await getDoc(reviewRef);

      if (docSnap.exists()) {
        return MatchReview.fromJSON({ id: docSnap.id, ...docSnap.data() });
      }
      return null;
    } catch (error) {
      console.error('Error fetching match review:', error);
      throw error;
    }
  }

  async getMatchReviewsForDraft(draftId) {
    if (this.demoMode) {
      const reviews = this.demoStorage.matchReviews || {};
      return Object.values(reviews)
        .filter(review => review.draftId === draftId)
        .map(review => MatchReview.fromJSON(review));
    }

    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      const q = query(
        collection(db, 'matchReviews'),
        where('draftId', '==', draftId),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const reviews = [];
      querySnapshot.forEach(doc => {
        reviews.push(MatchReview.fromJSON({ id: doc.id, ...doc.data() }));
      });

      return reviews;
    } catch (error) {
      console.error('Error fetching match reviews:', error);
      throw error;
    }
  }

  async deleteMatchReview(reviewId) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Deleting match review:', reviewId);
      const reviews = this.demoStorage.matchReviews || {};
      delete reviews[reviewId];
      return;
    }

    try {
      await deleteDoc(doc(db, 'matchReviews', reviewId));
      console.log('Match review deleted:', reviewId);
    } catch (error) {
      console.error('Error deleting match review:', error);
      throw error;
    }
  }

  // ============= SHARED DRAFT ACCESS =============

  async getDraftByShareToken(shareToken) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Firestore getDraftByShareToken skipped (commercial-demo: local-only build)');
      return null;
    }
    if (this.demoMode) {
      console.log('📝 Demo mode: Getting draft by share token:', shareToken);
      const shareLinks = this.demoStorage.shareLinks || {};
      const shareLink = shareLinks[shareToken];
      if (!shareLink) return null;

      // Get the draft - need to search through all users' drafts
      const drafts = this.demoStorage.drafts || {};
      const draftId = shareLink.draftId;

      // Search through all users to find the draft
      for (const userId in drafts) {
        if (drafts[userId][draftId]) {
          return drafts[userId][draftId];
        }
      }

      return null;
    }

    try {
      // First get the share link to find the draft ID
      const shareLink = await this.getShareLink(shareToken);
      if (!shareLink) {
        throw new Error('Share link not found');
      }

      // Then get the draft
      const draftRef = doc(db, 'drafts', shareLink.draftId);
      const draftSnap = await getDoc(draftRef);

      if (draftSnap.exists()) {
        return { id: draftSnap.id, ...draftSnap.data() };
      }

      return null;
    } catch (error) {
      console.error('Error getting draft by share token:', error);
      throw error;
    }
  }

  // ============= USER TIER MANAGEMENT =============

  /**
   * Get user profile data including tier
   */
  async getUserProfile(userId) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Getting user profile');
      return {
        tier: 'premium', // Demo mode users get premium
        isAdmin: true,
        matchPullsToday: 0,
        lastMatchPullDate: null
      };
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      // Return default free tier if no profile exists
      return {
        tier: 'free',
        isAdmin: false,
        matchPullsToday: 0,
        lastMatchPullDate: null
      };
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  /**
   * Update user tier (admin only)
   */
  async updateUserTier(userId, tier) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Updating user tier:', userId, tier);
      return;
    }

    try {
      await updateDoc(doc(db, 'users', userId), {
        tier: tier
      });
      console.log('User tier updated:', userId, tier);
    } catch (error) {
      console.error('Error updating user tier:', error);
      throw error;
    }
  }

  /**
   * Update tier in allowedEmails collection (for users who haven't signed in yet)
   */
  async updateAllowedEmailTier(email, tier) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Updating allowed email tier:', email, tier);
      return;
    }

    try {
      await updateDoc(doc(db, 'allowedEmails', email), {
        tier: tier
      });
      console.log('Allowed email tier updated:', email, tier);
    } catch (error) {
      console.error('Error updating allowed email tier:', error);
      throw error;
    }
  }

  /**
   * Update user's last active timestamp
   */
  async updateLastActive(userId) {
    if (this.demoMode) {
      return;
    }

    try {
      await updateDoc(doc(db, 'users', userId), {
        lastActive: serverTimestamp()
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.warn('Failed to update lastActive:', error);
    }
  }

  /**
   * Get all users (admin only)
   */
  async getAllUsers() {
    if (this.demoMode) {
      console.log('📝 Demo mode: Getting all users');
      return [];
    }

    try {
      // Try with orderBy first, fall back to unordered query if createdAt index doesn't exist
      let q;
      try {
        q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const users = [];
        querySnapshot.forEach(doc => {
          users.push({ id: doc.id, ...doc.data() });
        });
        return users;
      } catch (indexError) {
        // If index doesn't exist, query without orderBy
        console.warn('createdAt index not available, fetching users without ordering:', indexError);
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        const users = [];
        querySnapshot.forEach(doc => {
          users.push({ id: doc.id, ...doc.data() });
        });
        return users;
      }
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  }

  /**
   * Track match pull for daily limit
   */
  async incrementMatchPull(userId) {
    if (this.demoMode) {
      console.log('📝 Demo mode: Incrementing match pull count');
      return;
    }

    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const lastPullDate = userData.lastMatchPullDate;

        // Reset counter if it's a new day
        if (lastPullDate !== today) {
          await updateDoc(userRef, {
            matchPullsToday: 1,
            lastMatchPullDate: today
          });
        } else {
          // Increment counter
          await updateDoc(userRef, {
            matchPullsToday: (userData.matchPullsToday || 0) + 1
          });
        }
      }
    } catch (error) {
      console.error('Error incrementing match pull count:', error);
      throw error;
    }
  }

  /**
   * Get match pull count for today
   */
  async getMatchPullsToday(userId) {
    if (this.demoMode) {
      return 0;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const today = new Date().toISOString().split('T')[0];
        const lastPullDate = userData.lastMatchPullDate;

        // Return 0 if last pull was on a different day
        if (lastPullDate !== today) {
          return 0;
        }

        return userData.matchPullsToday || 0;
      }
      return 0;
    } catch (error) {
      console.error('Error getting match pull count:', error);
      return 0;
    }
  }

  /**
   * Upgrade all existing users to Premium tier (admin only)
   */
  async upgradeAllUsersToPremium() {
    if (this.demoMode) {
      console.log('📝 Demo mode: Upgrading all users to premium');
      return { upgraded: 0, alreadyPremium: 0, errors: 0 };
    }

    try {
      const users = await this.getAllUsers();
      let upgraded = 0;
      let alreadyPremium = 0;
      let errors = 0;

      for (const user of users) {
        try {
          if (user.tier === 'premium') {
            alreadyPremium++;
          } else {
            await updateDoc(doc(db, 'users', user.id), {
              tier: 'premium',
              upgradedAt: new Date().toISOString(),
              upgradedReason: 'Existing user - grandfathered to Premium'
            });
            upgraded++;
          }
        } catch (error) {
          console.error(`Error upgrading user ${user.id}:`, error);
          errors++;
        }
      }

      return { upgraded, alreadyPremium, errors, total: users.length };
    } catch (error) {
      console.error('Error upgrading users:', error);
      throw error;
    }
  }
}

const firebaseService = new FirebaseService();
export default firebaseService; 